import React from 'react';
import { NavLink } from 'react-router-dom';
import styled from 'styled-components';

import UpdateBanner from './UpdateBanner';

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  background: var(--tg-bg);
  color: var(--tg-text);
`;

const Header = styled.header`
  height: 56px;
  padding-top: var(--safe-top);
  padding-left: calc(12px + var(--safe-left));
  padding-right: calc(12px + var(--safe-right));
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: var(--tg-surface);
  border-bottom: 1px solid var(--tg-border);
`;

const Title = styled.div`
  font-weight: 700;
  font-size: 16px;
  letter-spacing: 0.2px;
`;

const Content = styled.main`
  flex: 1;
  overflow: auto;
  padding-bottom: calc(64px + var(--safe-bottom));
`;

const BottomNav = styled.nav`
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 50;
  padding-bottom: var(--safe-bottom);
  background: transparent;
`;

const BottomNavInner = styled.div`
  max-width: 520px;
  margin: 0 auto;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-around;
  background: var(--tg-surface);
  border-top: 1px solid var(--tg-border);
  backdrop-filter: saturate(180%) blur(12px);
`;

const Tab = styled(NavLink)`
  flex: 1;
  height: 64px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  text-decoration: none;
  color: var(--tg-muted);
  font-size: 11px;
  letter-spacing: 0.1px;

  &.active {
    color: var(--tg-accent);
  }
`;

const Icon = styled.div`
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  svg {
    width: 22px;
    height: 22px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const Fab = styled(NavLink)`
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  margin-top: -26px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--tg-surface);
  border: 1px solid var(--tg-border);
  box-shadow: var(--tg-shadow);
  text-decoration: none;
  color: var(--tg-accent);

  &:focus-visible {
    outline: 2px solid rgba(42, 158, 241, 0.35);
    outline-offset: 2px;
  }

  svg {
    width: 26px;
    height: 26px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.9;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const BubbleIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M20 12a7 7 0 0 1-7 7H8l-4 3 1.3-4.2A7 7 0 0 1 4 12a8 8 0 0 1 16 0Z" />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 5v14" />
    <path d="M5 12h14" />
  </svg>
);

const PhoneIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.4 19.4 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.8.3 1.6.6 2.4a2 2 0 0 1-.5 2.1L8 9.4a16 16 0 0 0 6.6 6.6l1.2-1.2a2 2 0 0 1 2.1-.5c.8.3 1.6.5 2.4.6a2 2 0 0 1 1.7 2Z" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M16 11a4 4 0 1 0-8 0" />
    <path d="M4 20a6 6 0 0 1 16 0" />
    <path d="M18 8.5a3 3 0 1 0-2.7-4.2" />
    <path d="M20 20a5.5 5.5 0 0 0-4-5.2" />
  </svg>
);

const GearIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z" />
    <path d="M19.4 15a7.9 7.9 0 0 0 .1-2l2-1.2-2-3.5-2.2.6a8.2 8.2 0 0 0-1.7-1l-.3-2.3H9.7L9.4 8a8.2 8.2 0 0 0-1.7 1L5.5 8.4l-2 3.5 2 1.2a7.9 7.9 0 0 0 .1 2l-2 1.2 2 3.5 2.2-.6a8.2 8.2 0 0 0 1.7 1l.3 2.3h5.6l.3-2.3a8.2 8.2 0 0 0 1.7-1l2.2.6 2-3.5-2-1.2Z" />
  </svg>
);

const AppShell = ({ title, right, children }) => {
  return (
    <Shell>
      <Header>
        <Title>{title}</Title>
        <div>{right}</div>
      </Header>

      <UpdateBanner />

      <Content>{children}</Content>

      <BottomNav>
        <BottomNavInner>
          <Tab to="/" end>
            <Icon><BubbleIcon /></Icon>
            چت
          </Tab>
          <Tab to="/users">
            <Icon><UsersIcon /></Icon>
            کاربران
          </Tab>

          <Fab to="/rooms" aria-label="اتاق‌ها">
            <PlusIcon />
          </Fab>

          <Tab to="/calls">
            <Icon><PhoneIcon /></Icon>
            تماس
          </Tab>
          <Tab to="/settings">
            <Icon><GearIcon /></Icon>
            تنظیمات
          </Tab>
        </BottomNavInner>
      </BottomNav>
    </Shell>
  );
};

export default AppShell;
